import React, { useEffect, useMemo, useRef, useState } from "react";

import { InertiaLink, usePage } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import { DynamicInputType } from "@/Components/Questions/InputTypes/Report";
import { focusAtom } from "jotai-optics";
import { reportQuestionsAtom } from "@/Stores";
import { useAtom } from "jotai";
import { DateSelectSpan, Dialog, Formheader } from "@/Components";
import { LabeledInput } from "@/Components/Form";
import { toast } from "react-toastify";
import { copyToClipboard, formatDate } from "@/Utilities/Utilities";
import { ReportHistory } from "@/Components/Questions/ReportHistory";
import { QUESTION_TYPES } from "@/Enums/question_types";
import { Tab, Tabs } from "@/Components/Tabs";
import CodeBlock from "@/Components/Blocks/CodeBlock";
import { startOfToday, subDays } from "date-fns";
import { ROLE_ID } from "@/Enums/roles";

export function ReportQuestion({ question: _question, derivedTeam = null }) {
  const team = derivedTeam || usePage().props?.team?.data || {};

  const { auth } = usePage().props;

  const inputAtom = useMemo(
    () => focusAtom(reportQuestionsAtom, (optic) => optic.prop(_question.id)),
    [_question],
  );
  const [payload, setPayload] = useAtom(inputAtom);

  const question = {
    ..._question,
    comment: "",
    errorBag: `reportQuestion.${_question.id}`,
  };

  const [disabled, setDisabled] = useState(false);

  const historyRef = useRef(null);

  useEffect(() => {
    // if (!questionTypes?.data?.length) {
    //   Inertia.reload({ only: ['questionTypes'] });
    // }
    //
    // fetch(`/questions/${_question.id}`)
    //   .then((response) => response.json())
    //   .then((data) => {
    //     setQuestion({
    //       ...data,
    //       comment: '',
    //       errorBag: `reportQuestion.${_question.id}`,
    //     }
    //     );
    //   });

    setPayload((prev) => ({
      ...prev,
      start_date: new Date(),
      end_date: new Date(),
    }));
  }, []);

  const isDisabled = () => {
    if (disabled) return true;

    const hasAnswers = payload?.answers?.length > 0;
    const hasComment =
      payload?.comment !== undefined && payload?.comment !== "";
    const hasEmptyAnswer = payload?.answers?.some(
      (answer) => answer?.value === undefined || answer?.value === "",
    );

    return !hasAnswers || !hasComment || hasEmptyAnswer;
  };

  const handleFormUpdate = (e) => {
    const { name, value } = e.target;
    setPayload((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNewActivity = (e) => {
    setDisabled(true);

    const startDate = payload.start_date
      ? formatDate(payload.start_date.toISOString(), "yyyy-MM-dd")
      : null;
    const endDate = payload.end_date
      ? formatDate(payload.end_date.toISOString(), "yyyy-MM-dd")
      : null;

    const answerPayload = {
      comment: payload.comment,
      start_date: startDate,
      end_date: endDate,
      values: [],
    };

    payload.answers.map((answer) => {
      answerPayload.values.push({
        question_input_id: answer.question_input_id,
        value: answer.value,
      });
    });

    const id = toast.loading("Please wait...");

    Inertia.post(
      `/questions/${_question.id}/teams/${team.id}/answers`,
      answerPayload,
      {
        preserveScroll: true,
        only: ["errors", "team"],
        onSuccess: () => {
          toast.update(id, {
            render: "Your answer has been successfully registered",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
          });
          setPayload((prev) => ({
            start_date: prev.start_date,
            end_date: prev.end_date,
          }));

          historyRef.current?.refreshHistory();
        },
        onError: (error) => {
          toast.update(id, {
            render: "Something went wrong, please review your input",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
          });
        },
        onFinish: () => {
          setDisabled(false);
        },
      },
    );

    e.preventDefault();
  };

  if (!question || !payload) return <div>Loading...</div>;

  let tabs = ["Reporters", "Calculators", "Webhooks"];

  return (
    <div className="mb-8 flex flex-col gap-4">
      <div className="shadow-small flex flex-col gap-4 bg-gray-light p-8">
        <div className="w-full">
          <Formheader>Choose period and add your report</Formheader>
        </div>

        {question?.type?.enum !== QUESTION_TYPES.Historical && (
          <div className="w-full: mb-4 md:w-80">
            <span className="mb-1 block">Reporting period</span>
            <DateSelectSpan
              value={{ from: payload.start_date, to: payload.end_date }}
              buttons={[
                "clear",
                "week",
                "month",
                "year",
                "lastWeek",
                "lastMonth",
                "lastYear",
              ]}
              onChange={(value) => {
                handleFormUpdate({
                  target: { name: "start_date", value: value.from },
                });
                handleFormUpdate({
                  target: { name: "end_date", value: value.to },
                });
              }}
            />
          </div>
        )}

        <DynamicInputType question={question} />

        <LabeledInput
          name="comment"
          title="Add comment or link to documentation*"
          placeholder="Comment or method"
          className="mb-4"
          value={payload?.comment || ""}
          onChange={handleFormUpdate}
        />

        <div className="flex w-full flex-row justify-end gap-4">
          <button
            disabled={isDisabled()}
            className="btn-blue flex-grow-0"
            onClick={handleNewActivity}
          >
            Report
          </button>
        </div>
      </div>

      <Tabs items={tabs}>
        <Tab>
          <ReportHistory question={question} team={team} ref={historyRef} />
        </Tab>
        <Tab>
          <ReportHistory
            question={question}
            team={team}
            type="calculator_recipient"
          />
        </Tab>
        <Tab>
          <div className="w-full">
            {auth.user.data.organization_role_id !== ROLE_ID.REPORTER && (
              <Webhook question={question} team={team} />
            )}
            <ReportHistory question={question} team={team} type="webhook" />
          </div>
        </Tab>
      </Tabs>
    </div>
  );
}

function Webhook({ question, team }) {
  const [openModal, setOpenModal] = useState(false);

  const handleActivate = () => {
    const id = toast.loading("Please wait...");
    Inertia.post(route("webhook.store", [team, question]), null, {
      onSuccess: () => {
        toast.update(id, {
          render: "Webhook activated",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
        });
      },
      onError: () => {
        toast.update(id, {
          render: "Something went wrong, please review your input",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
        });
      },
    });
  };

  const handleDelete = () => {
    const id = toast.loading("Please wait...");
    Inertia.delete(route("webhook.destroy", [team, question]), {
      onSuccess: () => {
        toast.update(id, {
          render: "Webhook deactivated",
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 5000,
        });
      },
      onError: () => {
        toast.update(id, {
          render: "Something went wrong, please review your input",
          type: toast.TYPE.ERROR,
          isLoading: false,
          autoClose: 5000,
        });
      },
    });
  };

  const handleCopy = () => {
    copyToClipboard(question.webhooks[0].url).then((success) => {
      if (success) {
        toast.success("Webhook link copied to clipboard");
      } else {
        toast.error("Failed to copy webhook link to clipboard");
      }
    });
  };

  function InformationSidebar() {
    const isMulti = question.meta?.select_type === "multi" || false;
    const requireValue = question?.meta?.require_value || false;

    // check if any of the inputs has an emission_factor
    const hasEmissionFactor = question.inputs.some(
      (input) => input.emission_factor,
    );

    function generatePostCodeBlock() {
      let activityInfo = `/**
* Represents a ${QUESTION_TYPES.toString(question.type.enum)} activity.
* The question posed is: "${question.question_text || question.inputs[0].label}".
* ${question.type.enum === QUESTION_TYPES.Select ? `Each report ${isMulti ? "can contain multiple inputs but at least one" : "requires a single input"}, which must be ${requireValue || hasEmissionFactor ? "a numeric value." : "null"}` : ""}${question.type.enum === QUESTION_TYPES.Distribute ? "Each report requires  multiple inputs, which must be a numeric value." : ""}${question.type.enum === QUESTION_TYPES.Calculate ? "Each report requires a single input, which must be a numeric value." : ""}${question.type.enum === QUESTION_TYPES.FreeText ? "Each report requires a single input, which must be a string value" : ""}
**/`;

      let payloadInfo = `{
  "start_date": "required|nullable|date|date_format:Y-m-d",
  "end_date": "sometimes|nullable|date|date_format:Y-m-d",
  "comment": "sometimes|nullable|string",`;

      question.inputs.forEach((input) => {
        let rule = "required|string";
        if (
          question.type.enum === QUESTION_TYPES.Calculate ||
          question.type.enum === QUESTION_TYPES.Distribute
        ) {
          rule = "required|numeric";
        } else if (question.type.enum === QUESTION_TYPES.Select) {
          rule = `sometimes|at_least_one}|${requireValue ? "" : "nullable|"}numeric`;
        }

        const unit = input.unit ? `, unit: "${input.unit}"` : "";

        payloadInfo += `\n  "input_${input.id}": "${rule}", //Input option: "${input.label}"${unit}`;
      });

      payloadInfo += "\n}";

      return `${activityInfo}\n\n${payloadInfo}`;
    }

    function generateUrlCodeBlock() {
      let url = question.webhooks[0].url;

      const args = {
        start_date: formatDate(
          subDays(startOfToday(), 7).toISOString(),
          "yyyy-MM-dd",
        ),
        end_date: formatDate(startOfToday().toISOString(), "yyyy-MM-dd"),
        comment: "This is an example",
      };

      question.inputs.forEach((input) => {
        let rule = "value";

        if (
          question.type.enum === QUESTION_TYPES.Calculate ||
          question.type.enum === QUESTION_TYPES.Distribute
        ) {
          rule = 10;
        } else if (question.type.enum === QUESTION_TYPES.Select) {
          rule = requireValue ? 10 : null;
        }

        args[`input_${input.id}`] = rule;
      });

      url +=
        "&" +
        Object.keys(args)
          .map((key) => key + "=" + encodeURIComponent(args[key]))
          .join("&");

      return url;
    }

    const handleCopy = (text) => {
      copyToClipboard(text).then((success) => {
        if (success) {
          toast.success("Copied to clipboard", {
            position: toast.POSITION.TOP_LEFT,
          });
        } else {
          toast.error("Failed to copy to clipboard", {
            position: toast.POSITION.TOP_LEFT,
          });
        }
      });
    };

    return (
      <Dialog
        openModal={openModal}
        closeModal={() => setOpenModal(false)}
        title="Webhook information"
      >
        <div className="flex flex-col gap-2">
          <p>
            Below is the detailed payload for this activity. You can use this
            information to report on the activity in Emma from other automations
            or systems
          </p>
          <p>
            Please copy the text below and send it to the appropriate person for
            further action.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => handleCopy(generatePostCodeBlock())}
              className="btn btn-blue grow-0"
            >
              Copy
            </button>
          </div>
          <CodeBlock type="POST application/json">
            {generatePostCodeBlock()}
          </CodeBlock>

          <div className="mt-4 flex justify-end">
            <button
              onClick={() => handleCopy(generateUrlCodeBlock())}
              className="btn btn-blue grow-0"
            >
              Copy
            </button>
          </div>
          <CodeBlock type="GET">{generateUrlCodeBlock()}</CodeBlock>
        </div>
      </Dialog>
    );
  }

  return (
    <>
      <div className="shadow-small mb-4 flex flex-col gap-4 bg-gray-light px-8 pb-8 pt-4">
        <div className=" w-full border-b pb-4">
          <Formheader>Webhook</Formheader>
          <p className="max-w-none">
            Incoming webhooks are a simple way to report on activities from
            external sources directly into Emma.{" "}
            <InertiaLink
              className="text-blue underline hover:no-underline"
              href={route("docs.webhooks")}
            >
              Read more
            </InertiaLink>
          </p>
        </div>
        {question.webhooks.length === 0 && (
          <div className="flex flex-row justify-end">
            <button onClick={handleActivate} className="btn btn-blue grow-0">
              Create webhook
            </button>
          </div>
        )}

        {question.webhooks.length > 0 && (
          <div className="flex flex-row justify-end gap-4">
            <button
              onClick={handleDelete}
              className="text-blue underline hover:no-underline"
            >
              Delete webhook
            </button>
            <button
              onClick={() => setOpenModal((prev) => !prev)}
              className="btn btn-blue grow-0"
            >
              Show info
            </button>
            <button onClick={handleCopy} className="btn btn-blue grow-0">
              Copy link
            </button>
          </div>
        )}
      </div>
      {openModal && question.webhooks.length > 0 && <InformationSidebar />}
    </>
  );
}
